<template>
  <div id="app" :class="['min-h-screen', authStore.organization?.isDemo ? 'demo-banner-active' : '']">
    <!-- Demo-mode banner — pinned above the (fixed) sidebar; the demo-banner-active class on #app
         shifts every fixed top-0 element, and the sticky mobile header, down by the banner height
         so nothing overlaps. The text wraps on phones (the link shortens to "Sign up →" there so
         the message keeps most of the width, and main.css gives links a 44px minimum height below
         768px), so the real height is measured into --demo-banner-h (see the watcher below) and
         every offset reads that variable. -->
    <Teleport to="body">
      <div
        v-if="authStore.organization?.isDemo"
        ref="bannerEl"
        class="demo-banner fixed top-0 inset-x-0 z-[60] min-h-9 py-1.5 bg-orange-500 text-white text-xs sm:text-sm flex items-center justify-between gap-4 px-4"
      >
        <span>
          <strong>Demo mode:</strong>
          you’re browsing a sample workspace. All changes are blocked.
        </span>
        <router-link
          to="/signup"
          class="underline font-semibold whitespace-nowrap"
        >Sign up<span class="hidden sm:inline"> to use your own data</span> →</router-link>
      </div>
    </Teleport>

    <RouterView />
    <ToastNotifications />
    <AttachmentPreviewModal />

    <!-- Subscription lockout: the locked-out screen over everything, blocking the app (the router
         refuses app routes while locked; everything behind is inert and can't scroll) until Sign out
         or a successful Try again. -->
    <Teleport to="body">
      <div
        v-if="authStore.subscriptionLockedOut"
        ref="lockOverlay"
        role="dialog"
        aria-modal="true"
        aria-label="Your access is paused"
        tabindex="-1"
        class="fixed inset-0 z-[9999] overflow-auto bg-paper focus:outline-none"
      >
        <LockedOutScreen
          :reason="lockoutReason(authStore.subscriptionLockedOut)"
          :message="authStore.subscriptionLockedOut"
          :email="authStore.user?.email"
          :org-name="lockOrgName"
          :owner="lockOwner"
          :free-seats="lockFreeSeats"
          :signing-out="signingOut"
          :retrying="retrying"
          :retry-notice="retryNotice"
          @signout="signOut"
          @retry="retry"
        />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { RouterView } from 'vue-router'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useAuthStore } from './stores/auth'
import { useUIStore } from './stores/ui'
import ToastNotifications from './components/ui/ToastNotifications.vue'
import AttachmentPreviewModal from './components/common/AttachmentPreviewModal.vue'
import LockedOutScreen from './components/auth/LockedOutScreen.vue'
import { lockoutReason } from './components/auth/lockedOutModel'
import { useLockedOut } from './composables/useLockedOut'
import { lockBodyScroll } from './lib/bodyScrollLock'

const authStore = useAuthStore()
const uiStore = useUIStore()

// The demo banner can wrap to two lines on a phone, so its height isn't a constant. Publish the
// measured height as --demo-banner-h on <html> for the offsets in the style block below; the
// variable only exists while the banner does.
const bannerEl = ref<HTMLElement | null>(null)
let bannerObserver: ResizeObserver | null = null

function clearBannerHeight() {
  bannerObserver?.disconnect()
  bannerObserver = null
  document.documentElement.style.removeProperty('--demo-banner-h')
}

watch(
  bannerEl,
  (el) => {
    clearBannerHeight()
    if (!el) return
    const publish = () => document.documentElement.style.setProperty('--demo-banner-h', `${el.offsetHeight}px`)
    publish()
    if (typeof ResizeObserver !== 'undefined') {
      bannerObserver = new ResizeObserver(publish)
      bannerObserver.observe(el)
    }
  },
  { flush: 'post' },
)

onBeforeUnmount(clearBannerHeight)

// The lockout overlay. While it is up: the page can't scroll (the shared lock, released when it
// goes and on unmount), everything else on <body> is inert (the app, toasts, open dialogs, the
// tour; layers added later too) so the keyboard can't reach it, focus moves into the overlay, and
// the owner / org / seat details are fetched for the screen.
const {
  owner: lockOwner,
  orgName: lockOrgName,
  freeSeats: lockFreeSeats,
  signingOut,
  retrying,
  retryNotice,
  loadInfo: loadLockoutInfo,
  reset: resetLockedOut,
  signOut,
  retry,
} = useLockedOut()
const lockOverlay = ref<HTMLElement | null>(null)
let releaseLockScroll: (() => void) | null = null
let inertLayers: Element[] = []
let layerObserver: MutationObserver | null = null

function makeOthersInert(overlay: Element) {
  const apply = (el: Element) => {
    // Leave alone what was already inert (not ours to undo) and the overlay itself.
    if (el === overlay || el.tagName === 'SCRIPT' || el.hasAttribute('inert')) return
    el.setAttribute('inert', '')
    inertLayers.push(el)
  }
  Array.from(document.body.children).forEach(apply)
  layerObserver = new MutationObserver((records) => {
    for (const record of records) record.addedNodes.forEach((node) => node instanceof Element && apply(node))
  })
  layerObserver.observe(document.body, { childList: true })
}

function releaseLockout() {
  releaseLockScroll?.()
  releaseLockScroll = null
  layerObserver?.disconnect()
  layerObserver = null
  inertLayers.forEach((el) => el.removeAttribute('inert'))
  inertLayers = []
}

watch(
  () => !!authStore.subscriptionLockedOut,
  async (locked) => {
    if (!locked) {
      releaseLockout()
      resetLockedOut()
      return
    }
    releaseLockScroll ??= lockBodyScroll()
    loadLockoutInfo()
    await nextTick()
    const overlay = lockOverlay.value
    if (!overlay || !authStore.subscriptionLockedOut) return
    if (!layerObserver) makeOthersInert(overlay)
    overlay.focus()
  },
  { immediate: true },
)

onBeforeUnmount(releaseLockout)

onMounted(() => {
  if ((window as { __PRERENDER_INJECTED?: { isPrerender?: boolean } }).__PRERENDER_INJECTED?.isPrerender) return
  // Load UI preferences first (applies compact mode class)
  uiStore.loadPreferences()
  // Then initialize auth
  authStore.initializeAuth()
})
</script>

<style>
/* Demo-mode banner adjustments — the banner is fixed at top-0; everything else
   that's `fixed top-0` (the app sidebar, marketing nav) gets pushed down by
   the banner height so the layout doesn't visually overlap. The sticky mobile
   header opts in with .demo-sticky-header (a bare `.sticky.top-0` selector would
   also catch table headers and other sticky elements). --demo-banner-h is the
   banner's measured height (set in the script above); 2.25rem is the single-line
   height, used until it's measured. */
.demo-banner-active aside.fixed.top-0 { top: var(--demo-banner-h, 2.25rem) !important; height: calc(100% - var(--demo-banner-h, 2.25rem)) !important; }
.demo-banner-active nav.fixed.top-0  { top: var(--demo-banner-h, 2.25rem) !important; }
.demo-banner-active main             { padding-top: var(--demo-banner-h, 2.25rem); }
.demo-banner-active .demo-sticky-header { top: var(--demo-banner-h, 2.25rem); }
</style>
